/**
 * Condition Rules Engine
 *
 * Evaluates condition rules from flow.condition_rules and determines
 * which actions should be applied based on user responses.
 *
 * Supports:
 * - Multiple condition groups with AND/OR operators
 * - Multiple conditions within groups with AND/OR operators
 * - Actions: make_visible, make_invisible, make_required
 * - Scopes: question, option, event-dispatch
 * - Control types: option (select from options), value (text input)
 * - Rule types: equals, not_equals, contains, not_contains, regex
 *
 * Performance optimizations:
 * - Rules are indexed by trigger element keys for O(1) lookup
 * - Only rules affected by a changed element are re-evaluated
 * - Cached results are used for unchanged rules
 */

import find from 'lodash/find';
import findKey from 'lodash/findKey';

let cachedFlowId = null;
let cachedRuleIndex = null;
let cachedActionResults = new Map();

/**
 * Builds an index of rules by their trigger element keys
 * This allows O(1) lookup of which rules need to be evaluated when an element changes
 */
function buildRuleIndex(conditionRules) {
  const index = {
    byTriggerKey: new Map(), // elementKey -> Set of rule indices
    allRuleIndices: new Set(),
  };

  if (!conditionRules || !Array.isArray(conditionRules)) {
    return index;
  }

  conditionRules.forEach((rule, ruleIndex) => {
    index.allRuleIndices.add(ruleIndex);

    const triggerKeys = extractTriggerKeys(rule);
    triggerKeys.forEach((key) => {
      if (!index.byTriggerKey.has(key)) {
        index.byTriggerKey.set(key, new Set());
      }
      index.byTriggerKey.get(key).add(ruleIndex);
    });
  });

  return index;
}

function extractTriggerKeys(rule) {
  const keys = new Set();

  if (!rule.condition_groups) return keys;

  rule.condition_groups.forEach((group) => {
    if (!group.conditions) return;
    group.conditions.forEach((condition) => {
      const elementKey = condition.trigger_ref?.element_key || condition.element_key;
      if (elementKey) {
        keys.add(elementKey);
      }
    });
  });

  return keys;
}

function getRuleIndex(state) {
  const { conditionRules, id: flowId } = state.flow;

  if (flowId !== cachedFlowId) {
    cachedFlowId = flowId;
    cachedRuleIndex = null;
    cachedActionResults.clear();
  }

  if (!cachedRuleIndex) {
    cachedRuleIndex = buildRuleIndex(conditionRules);
  }

  return cachedRuleIndex;
}

function getAffectedRuleIndices(state, changedElementKey) {
  const index = getRuleIndex(state);

  if (!changedElementKey) {
    // No specific element changed - evaluate all rules
    return index.allRuleIndices;
  }

  return index.byTriggerKey.get(changedElementKey) || new Set();
}

/**
 * Evaluates a single condition against the current flow state
 * @param {Object} condition - The condition to evaluate
 * @param {Object} state - The Redux flow state
 * @returns {boolean} - Whether the condition is met
 */
function evaluateCondition(condition, state) {
  const { elements, questions } = state.flow;

  const elementKey = condition.trigger_ref?.element_key || condition.element_key;
  if (!elementKey) return false;

  const elementId = findKey(elements, { key: elementKey });
  if (!elementId) return false;

  const element = elements[elementId];
  const question = questions[elementId];

  if (!question || question.response === null || question.response === undefined) {
    return false;
  }

  const { response } = question;
  const controlType = condition.control_type || 'option';
  const ruleType = condition.rule_type || 'equals';

  if (controlType === 'value') {
    return evaluateValueCondition(response, condition.answer_value, ruleType);
  }

  return evaluateOptionCondition(response, condition, element, state);
}

function evaluateValueCondition(response, answerValue, ruleType) {
  if (typeof response !== 'string') {
    response = String(response || '');
  }

  switch (ruleType) {
    case 'equals':
      return response === answerValue;
    case 'not_equals':
      return response !== answerValue;
    case 'contains':
      return response.includes(answerValue || '');
    case 'not_contains':
      return !response.includes(answerValue || '');
    case 'regex':
      try {
        const regex = new RegExp(answerValue || '', 'i');
        return regex.test(response);
      } catch (e) {
        console.error('Invalid regex in condition:', e);
        return false;
      }
    default:
      return false;
  }
}

function evaluateOptionCondition(response, condition, element, state) {
  const { elements } = state.flow;
  const optionKeys =
    condition.option_keys?.length > 0 ? condition.option_keys : condition.option_key ? [condition.option_key] : [];
  const optionKeysOperator = condition.option_keys_operator || 'and';
  const statementKeys = condition.statement_keys || (condition.statement_key ? [condition.statement_key] : []);

  if (optionKeys.length === 0) return false;

  if (element.question?.style === 'matrix') {
    return evaluateMatrixCondition(response, optionKeys, optionKeysOperator, statementKeys, element);
  }

  if (!element.question?.options) return false;

  const responseArray = Array.isArray(response) ? response : [response];

  if (optionKeysOperator === 'or') {
    return optionKeys.some((optionKey) => {
      const selectedOption = find(element.question.options, { key: optionKey });
      return selectedOption && responseArray.includes(selectedOption.id);
    });
  }

  return optionKeys.every((optionKey) => {
    const selectedOption = find(element.question.options, { key: optionKey });
    return selectedOption && responseArray.includes(selectedOption.id);
  });
}

/**
 * Evaluates a matrix question condition
 * @param {Array} response - The user's response (array of statement selections)
 * @param {Array} optionKeys - The option keys to check for
 * @param {string} optionKeysOperator - 'and' or 'or' for how to combine option checks
 * @param {Array} statementKeys - The statement keys to filter by (if any)
 * @param {Object} element - The question element
 */
function evaluateMatrixCondition(response, optionKeys, optionKeysOperator, statementKeys, element) {
  if (!Array.isArray(response)) return false;

  const statements = element.question?.statements || [];
  const options = element.question?.options || [];

  const isOptionSelectedForAnyStatement = (optionKey) => {
    const selectedOption = find(options, { key: optionKey });
    if (!selectedOption) return false;

    const statementsToCheck =
      statementKeys.length > 0 ? statementKeys.map((key) => find(statements, { key })).filter(Boolean) : statements;

    if (statementsToCheck.length === 0) return false;

    return statementsToCheck.some((statement) =>
      response.some((res) => {
        if (typeof res !== 'object') return false;
        if (res.statementId !== statement.id) return false;

        const optionIndex = options.findIndex((o) => o.key === optionKey);
        const statementOption = statement.options?.[optionIndex];
        return (
          (statementOption && res.statementOptionId === statementOption.id) ||
          res.statementOptionId === selectedOption.id
        );
      }),
    );
  };

  if (optionKeysOperator === 'or') {
    return optionKeys.some((optionKey) => isOptionSelectedForAnyStatement(optionKey));
  }

  return optionKeys.every((optionKey) => isOptionSelectedForAnyStatement(optionKey));
}

function evaluateConditionGroup(group, state) {
  const { conditions, operator } = group;

  if (!conditions || conditions.length === 0) return false;

  if (operator === 'or') {
    return conditions.some((condition) => evaluateCondition(condition, state));
  }

  return conditions.every((condition) => evaluateCondition(condition, state));
}

function evaluateConditionGroups(rule, state) {
  const { condition_groups, condition_groups_operator } = rule;

  if (!condition_groups || condition_groups.length === 0) return false;

  if (condition_groups_operator === 'or') {
    return condition_groups.some((group) => evaluateConditionGroup(group, state));
  }

  return condition_groups.every((group) => evaluateConditionGroup(group, state));
}

/**
 * Gets all actions that should be applied based on current state
 * @param {Object} state - The Redux state
 * @param {string} changedElementKey - Optional: the key of the element that changed.
 *                                     If provided, only rules depending on this element are re-evaluated.
 *                                     Cached results are used for other rules.
 * @returns {Array} - Array of actions to apply
 */
export function getActionsToApply(state, changedElementKey = null) {
  const { conditionRules, elements, id: flowId } = state.flow;

  if (!conditionRules || !Array.isArray(conditionRules)) {
    return [];
  }

  const affectedIndices = getAffectedRuleIndices(state, changedElementKey);

  const actions = [];

  conditionRules.forEach((rule, ruleIndex) => {
    const { action } = rule;
    if (!action) return;

    let shouldApply;
    const cacheKey = `${flowId}-${rule.id}`;

    // Check if we need to re-evaluate this rule
    if (affectedIndices.has(ruleIndex)) {
      // Rule is affected - evaluate and cache
      shouldApply = evaluateConditionGroups(rule, state);
      cachedActionResults.set(cacheKey, shouldApply);
    } else if (cachedActionResults.has(cacheKey)) {
      // Use cached result
      shouldApply = cachedActionResults.get(cacheKey);
    } else {
      // No cache and not affected - evaluate and cache
      shouldApply = evaluateConditionGroups(rule, state);
      cachedActionResults.set(cacheKey, shouldApply);
    }

    const targetElementIds = (action.target_element_keys || [])
      .map((key) => findKey(elements, { key }))
      .filter(Boolean);

    const targetOptionInfo = (action.target_option_keys || [])
      .map((tok) => {
        const elementId = findKey(elements, { key: tok.question_key });
        const element = elements[elementId];
        const option = element?.question?.options?.find((o) => o.key === tok.option_key);
        return {
          elementId,
          optionId: option?.id,
          optionKey: tok.option_key,
          questionKey: tok.question_key,
        };
      })
      .filter((t) => t.elementId && t.optionId);

    const targetStatementInfo = (action.target_statement_keys || [])
      .map((tsk) => {
        const elementId = findKey(elements, { key: tsk.question_key });
        const element = elements[elementId];
        let statement = element?.question?.statements?.find((s) => s.key === tsk.statement_key);
        if (!statement) {
          statement = element?.question?.statements?.find((s) => {
            const label = s.body?.default || s.body?.en || Object.values(s.body || {})[0] || s.key;
            return label === tsk.statement_key;
          });
        }
        return {
          elementId,
          statementId: statement?.id,
          statementKey: tsk.statement_key,
          questionKey: tsk.question_key,
        };
      })
      .filter((t) => t.elementId && t.statementId);

    actions.push({
      ruleId: rule.id,
      shouldApply,
      action: action.type,
      scope: action.scope,
      targetElementIds,
      targetOptionInfo,
      targetStatementInfo,
      targetEvents: action.target_events || [],
    });
  });

  return actions;
}

export function getTriggeredEvents(state) {
  const actions = getActionsToApply(state);
  const events = [];

  actions.forEach((action) => {
    if (action.shouldApply && action.scope === 'event-dispatch' && action.targetEvents.length > 0) {
      events.push(...action.targetEvents);
    }
  });

  return events;
}

export function shouldElementBeVisible(state, elementId) {
  const actions = getActionsToApply(state);
  const element = state.flow.elements[elementId];

  if (!element) return true;

  let isVisible = element.defaultVisible !== undefined ? element.defaultVisible : element.isVisible;

  actions.forEach((action) => {
    if (action.scope !== 'question') return;
    if (!action.targetElementIds.includes(elementId)) return;

    if (action.shouldApply) {
      if (action.action === 'make_visible') {
        isVisible = true;
      } else if (action.action === 'make_invisible') {
        isVisible = false;
      }
    } else {
      if (action.action === 'make_visible') {
        isVisible = false;
      } else if (action.action === 'make_invisible') {
        isVisible = true;
      }
    }
  });

  return isVisible;
}

export function shouldElementBeRequired(state, elementId) {
  const actions = getActionsToApply(state);
  const element = state.flow.elements[elementId];
  const question = state.flow.questions[elementId];

  if (!element || !question) return question?.isRequired || false;

  let isRequired = question.defaultRequired !== undefined ? question.defaultRequired : question.isRequired;

  actions.forEach((action) => {
    if (action.scope !== 'question') return;
    if (!action.targetElementIds.includes(elementId)) return;
    if (action.action !== 'make_required') return;

    if (action.shouldApply) {
      isRequired = true;
    } else {
      // Condition not met - element is not dynamically required
      // Keep original required state
    }
  });

  return isRequired;
}

export function shouldOptionBeVisible(state, elementId, optionId) {
  const actions = getActionsToApply(state);
  const element = state.flow.elements[elementId];

  if (!element) return true;

  const option = element.question?.options?.find((o) => o.id === optionId);
  if (!option) return true;

  // Start with the option's default visibility
  let isVisible = option.defaultVisible !== undefined ? option.defaultVisible : option.isVisible;

  // Apply actions in order
  actions.forEach((action) => {
    if (action.scope !== 'option') return;

    const targetOption = action.targetOptionInfo.find((t) => t.elementId === elementId && t.optionId === optionId);
    if (!targetOption) return;

    if (action.shouldApply) {
      if (action.action === 'make_visible') {
        isVisible = true;
      } else if (action.action === 'make_invisible') {
        isVisible = false;
      }
    } else {
      // Condition not met - reverse the action
      if (action.action === 'make_visible') {
        isVisible = false;
      } else if (action.action === 'make_invisible') {
        isVisible = true;
      }
    }
  });

  return isVisible;
}

export function clearCache() {
  cachedFlowId = null;
  cachedRuleIndex = null;
  cachedActionResults.clear();
}

export default {
  getActionsToApply,
  getTriggeredEvents,
  shouldElementBeVisible,
  shouldElementBeRequired,
  shouldOptionBeVisible,
  evaluateConditionGroups,
  clearCache,
};
